import type { ConsentSignal } from "@/core/consent/domain/consent.signals";
import type {
  BannerPolicy,
  ConsentBasisProvenance,
  ConsentDecision,
  ConsentStatus,
  ResolvedConsentStatus,
  UnresolvedConsentStatus,
} from "@/core/consent/domain/consent.types";

/**
 * Pure consent engine: `(state, signal) → state`, no I/O, no vendors, no
 * clock. Every ignored or no-op signal returns the SAME state reference —
 * downstream memoization (and the bus's change detection) relies on
 * referential equality meaning "nothing happened".
 */

/**
 * The scopes Global Privacy Control governs. `functional` is deliberately
 * outside: GPC is an opt-out of tracking and sale/share, not a statement about
 * site functionality, which is why GPC alone can never resolve consent.
 */
const clampToGpc = (decision: ConsentDecision): ConsentDecision => ({
  ...decision,
  analytics: false,
  advertising: false,
  dataSharing: false,
});

const decisionsEqual = (a: ConsentDecision, b: ConsentDecision): boolean =>
  a.functional === b.functional &&
  a.analytics === b.analytics &&
  a.advertising === b.advertising &&
  a.dataSharing === b.dataSharing;

/**
 * The starting state for a page load. `noticeVersion` is data, passed in by
 * the composition root (which reads the committed `CONSENT_NOTICE_VERSION`);
 * the reducer itself never reaches for it, so tests can exercise a notice
 * bump by passing a different string.
 */
export const createInitialConsentState = (noticeVersion: string): UnresolvedConsentStatus => ({
  status: "unresolved",
  noticeVersion,
  gpcAsserted: false,
});

/**
 * Resolve an explicit answer under whatever GPC posture the state has
 * accumulated. The answer is kept verbatim; the EFFECTIVE decision is derived
 * from it: whenever GPC has been asserted, the governed scopes are clamped and
 * the effective provenance is `gpc` — mirroring the legacy recording rule
 * that GPC wins over both Banner and Override, because a browser-level
 * opt-out is not a banner interaction. Because only the answer is persisted,
 * the same answer restored on a visit WITHOUT GPC resolves un-clamped.
 */
const resolveWithGpc = (
  state: ConsentStatus,
  answer: ConsentDecision,
  answerProvenance: ConsentBasisProvenance,
  answerBannerPolicy: BannerPolicy | null,
): ResolvedConsentStatus => ({
  status: "resolved",
  noticeVersion: state.noticeVersion,
  gpcAsserted: state.gpcAsserted,
  answer,
  answerProvenance,
  answerBannerPolicy,
  decision: state.gpcAsserted ? clampToGpc(answer) : answer,
  provenance: state.gpcAsserted ? "gpc" : answerProvenance,
});

/** Whether the visitor has answered, as opposed to nothing or only a policy default being in effect. */
const isAnswered = (state: ConsentStatus): boolean =>
  state.status === "resolved" && state.answerProvenance !== "policyDefault";

export const reduceConsent = (state: ConsentStatus, signal: ConsentSignal): ConsentStatus => {
  switch (signal.type) {
    case "bannerAnswered": {
      // Re-reporting the same ANSWER (a reload, a second listener callback)
      // is a no-op, not a provenance flip: folding "an earlier record exists"
      // into the state would relabel an unchanged answer. Compared on the
      // answer, not the effective decision: under GPC a fresh answer that
      // merely happens to equal the clamp is still a new explicit decision
      // and must survive GPC being switched off later. An answer matching a
      // policy default is NOT a re-report: it is the visitor's own act.
      if (
        state.status === "resolved" &&
        isAnswered(state) &&
        decisionsEqual(state.answer, signal.decision)
      ) {
        return state;
      }

      // The first explicit answer on this browser is the banner's; the banner
      // never shows twice, so a later different answer can only have come from
      // the reopened preferences surface — an Override.
      return resolveWithGpc(
        state,
        signal.decision,
        isAnswered(state) ? "override" : "banner",
        signal.bannerPolicy,
      );
    }

    case "policyDefaultReported": {
      // Any answer, live or restored, outranks a default.
      if (isAnswered(state)) {
        return state;
      }

      // HubSpot re-reports on every consent callback; the same default under
      // the same policy is nothing new.
      if (
        state.status === "resolved" &&
        decisionsEqual(state.answer, signal.decision) &&
        state.answerBannerPolicy?.id === signal.bannerPolicy.id &&
        state.answerBannerPolicy.mode === signal.bannerPolicy.mode
      ) {
        return state;
      }

      return resolveWithGpc(state, signal.decision, "policyDefault", signal.bannerPolicy);
    }

    case "gpcAsserted": {
      if (state.gpcAsserted) {
        return state;
      }

      // GPC on an unresolved state does NOT resolve: it says nothing about
      // `functional`, and fabricating a full decision from GPC alone was
      // explicitly rejected in review (PR #13289). The assertion is retained
      // so a later banner answer (or restored decision) is clamped.
      if (state.status === "unresolved") {
        return { ...state, gpcAsserted: true };
      }

      // On a resolved state GPC clamps the EFFECTIVE decision and takes over
      // its provenance even when the values happen to be unchanged — a browser
      // opt-out arriving on top of a hand-clicked decline is a new, different
      // decision, and the distinction is exactly what provenance exists for.
      // The answer is untouched: it is what the visitor said, and it is what
      // comes back if GPC is switched off again.
      return {
        ...state,
        gpcAsserted: true,
        decision: clampToGpc(state.answer),
        provenance: "gpc",
      };
    }

    case "storedDecisionLoaded": {
      // A live answer this session outranks anything storage remembers; a
      // stored answer outranks a policy default.
      if (isAnswered(state)) {
        return state;
      }

      // Re-prompt by construction: a stored decision made under a different
      // notice generation is invalid evidence, so it is ignored at the engine
      // level and the state stays unresolved — no consumer needs its own
      // "should we re-ask" logic.
      if (signal.noticeVersion !== state.noticeVersion) {
        return state;
      }

      // Restores the ANSWER with its original provenance — a restored banner
      // answer is still a banner answer — and derives the effective decision
      // under THIS session's GPC posture, exactly as it would for a live
      // answer (order must not matter). A visit with GPC switched off gets
      // the explicit grant back; one with it on gets the clamp.
      return resolveWithGpc(state, signal.decision, signal.provenance, signal.bannerPolicy);
    }
  }
};
